import { useState } from "react";
import { Modal } from "antd";
import { AlertTriangle } from "lucide-react";
import Button from "../components/common/button/button";

type UseExportHandlerProps = {
  exportMutation: () => readonly [any, { isLoading: boolean }];
  listFetchParams: Record<string, any>;
  totalCount: number;
  exportLimit?: number;
  onRefineFilters?: () => void;
};

export const useExportHandler = ({
  exportMutation,
  listFetchParams,
  totalCount,
  exportLimit = 10000,
  onRefineFilters,
}: UseExportHandlerProps) => {
  const [exportExcel, { isLoading }] = exportMutation();
  const [showWarningModal, setShowWarningModal] = useState(false);

  const executeExport = async (limitToMax = false) => {
    try {
      const exportParams = { ...listFetchParams };

      delete exportParams.page;
      delete exportParams.limit;

      if (limitToMax) {
        exportParams.limit = exportLimit;
      }

      await exportExcel(exportParams).unwrap();

      setShowWarningModal(false);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleExport = async () => {
    if (totalCount > exportLimit) {
      setShowWarningModal(true);
      return;
    }

    await executeExport();
  };

  const ExportLimitModal = (
    <Modal
      title={
        <div className="flex items-center gap-2 text-amber-600 font-medium text-base">
          <AlertTriangle size={20} />
          <span>Export Limit Warning</span>
        </div>
      }
      open={showWarningModal}
      onCancel={() => setShowWarningModal(false)}
      footer={[
        <Button
          key="cancel"
          variant="outline"
           className="mr-2"
          onClick={() => {
            setShowWarningModal(false);
            onRefineFilters?.();
          }}
        >
          Refine Filters
        </Button>,
        <Button
          key="submit"
          disabled={isLoading}
          onClick={() => executeExport(true)}
        >
          {isLoading
            ? "Exporting..."
            : `Download First ${exportLimit.toLocaleString()} Rows`}
        </Button>,
      ]}
    >
      <div className="py-2 text-sm text-gray-600 leading-relaxed">
        Your current filter selection contains{" "}
        <span className="font-semibold text-gray-900">
          {totalCount.toLocaleString()}
        </span>{" "}
        records. To ensure smooth browser performance, Excel generation is
          limited to a maximum of{" "}
        <span className="font-semibold text-gray-900">
          {exportLimit.toLocaleString()}
        </span>{" "}
        rows per download.
         <br />
          <br />
          Would you like to export the first {exportLimit.toLocaleString()}{" "}
          records matching your current sort order, or close this window to
          narrow your date range or site configurations?
      </div>
    </Modal>
  );

  return {
    handleExport,
    isExportLoading: isLoading,
    ExportLimitModal,
  };
};